import { FC, ReactNode, useState } from "react";
import { useQuery } from "@apollo/client";
import { Alert, Box, Button, Typography } from "@mui/material";
import DevicesIcon from "@mui/icons-material/Devices";
import { useTranslation } from "@/shared/hooks";
import ViewSessionsQuery from "@/shared/graphql/queries/ViewSessionsQuery.graphql";
import { getViewerId } from "@/shared/utils/viewer";
import type { ViewSessionsQueryType } from "@/types/graphql/site.generated";
import { ViewSessionsDialog } from "./ViewSessionsDialog";
import type { ViewSessionNode } from "./viewSessions";

// Стабильная пустая ссылка: диалог сбрасывает выбор при смене списка.
const NO_SESSIONS: readonly ViewSessionNode[] = [];

interface ParallelLimitScreenProps {
  source: "SITE" | "EMBED";
  contentId: string;
  /** Адрес контента на витрине: embed ведёт туда выбирать устройство. */
  contentUrl?: string | null;
  poster?: string | null;
  /** «Смотреть здесь» удалось: плеер перезапрашивает доступ. */
  onTakenOver: () => void;
  /** Повторный запрос доступа после «Смотреть здесь» не удался. */
  startFailed?: boolean;
  assistantAction?: ReactNode;
}

/**
 * Экран «Смотрят на другом устройстве» (лимит параллельных просмотров
 * платного контента): вместо плеера, когда доступ, createView или
 * сердцебиение ответили лимитом. На витрине кнопка открывает список
 * устройств; в embed диалога нет, только ссылка на контент на сайте.
 */
export const ParallelLimitScreen: FC<ParallelLimitScreenProps> = ({
  source,
  contentId,
  contentUrl,
  poster,
  onTakenOver,
  startFailed = false,
  assistantAction,
}) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const isEmbed = source === "EMBED";
  const { data, loading, refetch } = useQuery<ViewSessionsQueryType>(
    ViewSessionsQuery,
    {
      variables: { contentId, viewerId: getViewerId() },
      // Список меняется на других устройствах: всегда с сервера.
      fetchPolicy: "network-only",
      skip: isEmbed,
    },
  );
  const limit = data?.viewSessions.limit;

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: "16 / 9",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "#000",
        backgroundImage: poster ? `url(${poster})` : undefined,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 1.5,
          backgroundColor: "rgba(0,0,0,0.65)",
          color: "#fff",
          textAlign: "center",
          px: 2,
        }}
      >
        <DevicesIcon sx={{ fontSize: 48 }} />
        <Typography variant="h6">{t("parallelLimit.title")}</Typography>
        <Typography variant="body2" sx={{ maxWidth: 480 }}>
          {isEmbed ? t("parallelLimit.embedText") : t("parallelLimit.text")}
        </Typography>
        {limit != null && (
          <Typography variant="body2">
            {t("parallelLimit.limit", { limit })}
          </Typography>
        )}
        {startFailed && (
          <Alert severity="error" sx={{ maxWidth: 480 }}>
            {t("parallelLimit.startError")}
          </Alert>
        )}
        {isEmbed && contentUrl && (
          <Button
            variant="contained"
            component="a"
            href={contentUrl}
            target="_blank"
            rel="noopener"
          >
            {t("parallelLimit.openOnSite")}
          </Button>
        )}
        {!isEmbed && (
          <Button
            variant="contained"
            onClick={() => {
              setDialogOpen(true);
              void refetch();
            }}
          >
            {t("parallelLimit.showDevices")}
          </Button>
        )}
        {assistantAction}
      </Box>
      {!isEmbed && (
        <ViewSessionsDialog
          open={dialogOpen}
          contentId={contentId}
          sessions={data?.viewSessions.nodes ?? NO_SESSIONS}
          loading={loading}
          onClose={() => setDialogOpen(false)}
          onTakenOver={() => {
            setDialogOpen(false);
            onTakenOver();
          }}
        />
      )}
    </Box>
  );
};
